/**
 * adminDataContext — the React Context + hook used by admin child routes
 * to read the data fetched by AdminLayout.
 *
 * Extracted from AdminLayout.jsx specifically so Vite React Fast Refresh
 * works cleanly during development: react-plugin-react requires modules
 * to export EITHER React components OR non-components, not a mix. When
 * this hook lived next to the AdminLayout component, every code edit in
 * the admin graph invalidated HMR and stuck the page in a loading state
 * until manual refresh. Moving the hook + context here keeps
 * AdminLayout.jsx as a components-only module.
 *
 * (https://github.com/vitejs/vite-plugin-react/tree/main/packages/plugin-react#consistent-components-exports)
 */

import { createContext, useContext } from 'react';

// Default context is a safe loading state — prevents null crashes in every
// consumer (child routes can render something meaningful before AdminLayout
// finishes fetching).
export const LOADING_STATE = {
  loading: true,
  // Core data
  annotators: [], jobs: [], assignments: [], qualityLogs: [],
  applications: [], clients: [], onboardingData: {}, juuniScores: {},
  allOrgs: [], splUsers: [], dbLog: [], batchSummary: {}, datasets: [],
  // SPL-scoped data
  myJobs: [], myAssignments: [], myApplications: [],
  isScopedRole: false, isScopeActive: false,
  ownJobIds: new Set(),
  viewScope: 'my_section', setViewScope: () => {},
  // Quality
  qualityIndexes: { byJob: {}, byIndividual: {}, byJobAndIndividual: {} },
  getAssignmentQuality: () => 0,
  getIndividualQuality: () => 0,
  // Alerts
  alerts: [], visibleAlerts: [],
  dismissAlert: () => {}, restoreAllAlerts: () => {},
  dismissedCount: 0,
  // Actions
  activeAdminOrgId: null, loadData: () => {}, logDbOp: () => {},
};

export const AdminDataContext = createContext(LOADING_STATE);

export const useAdminDataContext = () => useContext(AdminDataContext);
